import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { IconButton } from "../forms/IconButton.js";
import { cx } from "../internal/cx.js";
import { Menu, MenuGroup, MenuItem } from "../overlays/Menu.js";
const PERIOD_SWITCHER_LABELS = {
  group: "Period",
  select: "Period",
  previous: "Previous period",
  next: "Next period"
};
const PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/u;
function isPeriod(value) {
  return PERIOD_RE.test(value);
}
function parts(value) {
  const m = PERIOD_RE.exec(value);
  return m ? { y: Number(m[1]), m: Number(m[2]) } : null;
}
const dateFormats = /* @__PURE__ */ new Map();
function dateFormat(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = dateFormats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" });
    dateFormats.set(key, f);
  }
  return f;
}
function formatPeriodLong(value, locale) {
  const p = parts(value);
  if (!p) return value;
  return dateFormat(locale, { month: "long", year: "numeric" }).format(Date.UTC(p.y, p.m - 1, 1));
}
function monthShort(m, locale) {
  return dateFormat(locale, { month: "short" }).format(Date.UTC(2e3, m - 1, 1));
}
function ChevronIcon({ dir }) {
  const d = dir === "left" ? "m15 18-6-6 6-6" : dir === "right" ? "m9 18 6-6-6-6" : "m6 9 6 6 6-6";
  return /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx("path", { d }) });
}
const GRID_KEYS = /* @__PURE__ */ new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);
function moveInGrid(event, isRtl) {
  if (!GRID_KEYS.has(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const from = event.currentTarget;
  const root = from.closest('[role="menu"]');
  if (!root) return;
  const items = [...root.querySelectorAll("[data-period-cell]")];
  const at = items.indexOf(from);
  if (at === -1) return;
  event.preventDefault();
  event.stopPropagation();
  const pos = (el) => ({
    row: Number(el.dataset.yearIndex) * 3 + Number(el.dataset.row),
    col: Number(el.dataset.col)
  });
  let target;
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    const forward = event.key === "ArrowRight" !== isRtl;
    target = items[(at + (forward ? 1 : -1) + items.length) % items.length];
  } else {
    const down = event.key === "ArrowDown";
    const here = pos(from);
    const rows = [...new Set(items.map((el) => pos(el).row))].sort((a, b) => a - b);
    const ahead = rows.filter((r) => down ? r > here.row : r < here.row);
    const row = ahead.length > 0 ? down ? ahead[0] : ahead[ahead.length - 1] : down ? rows[0] : rows[rows.length - 1];
    let best = Infinity;
    for (const el of items) {
      const p = pos(el);
      if (p.row !== row) continue;
      const distance = Math.abs(p.col - here.col);
      if (distance < best) {
        best = distance;
        target = el;
      }
    }
  }
  target?.focus();
}
const PeriodSwitcher = React.forwardRef(function PeriodSwitcher2({ value, onChange, options, locale, picker = "grid", formatPeriod = formatPeriodLong, disabled, labels: labelsProp, className, ...rest }, ref) {
  const labels = { ...PERIOD_SWITCHER_LABELS, ...labelsProp };
  const id = React.useId();
  const sorted = React.useMemo(
    () => [...options].filter((o) => isPeriod(o.value)).sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0),
    [options]
  );
  const enabled = sorted.filter((o) => !o.disabled);
  const prev = [...enabled].reverse().find((o) => o.value < value);
  const next = enabled.find((o) => o.value > value);
  const current = sorted.find((o) => o.value === value);
  const label = (o) => o.state ? `${formatPeriod(o.value, locale)} · ${o.state}` : formatPeriod(o.value, locale);
  const years = React.useMemo(() => {
    const byYear = /* @__PURE__ */ new Map();
    for (const o of sorted) {
      const y = Number(o.value.slice(0, 4));
      byYear.set(y, [...byYear.get(y) ?? [], o]);
    }
    return [...byYear.entries()].sort((a, b) => b[0] - a[0]);
  }, [sorted]);
  return /* @__PURE__ */ jsxs("div", { ref, role: "group", "aria-label": labels.group, ...rest, className: cx("ev-period", className), children: [
    /* @__PURE__ */ jsx(IconButton, { "aria-label": labels.previous, title: labels.previous, disabled: disabled || !prev, onClick: () => prev && onChange(prev.value), children: /* @__PURE__ */ jsx(ChevronIcon, { dir: "left" }) }),
    picker === "select" ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("label", { htmlFor: id, className: "ev-visually-hidden", children: labels.select }),
      /* @__PURE__ */ jsxs("select", { id, className: "ev-select ev-period__select", value, disabled, onChange: (event) => onChange(event.target.value), children: [
        current ? null : /* @__PURE__ */ jsx("option", { value, children: formatPeriod(value, locale) }),
        [...sorted].reverse().map((o) => /* @__PURE__ */ jsx("option", { value: o.value, disabled: o.disabled, children: label(o) }, o.value))
      ] })
    ] }) : /* @__PURE__ */ jsx(
      Menu,
      {
        className: "ev-period__menu",
        align: "center",
        disabled,
        trigger: /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ev-btn ev-btn--ghost ev-period__trigger",
            "aria-label": `${labels.select}: ${current ? label(current) : formatPeriod(value, locale)}`,
            children: [
              /* @__PURE__ */ jsx("span", { children: formatPeriod(value, locale) }),
              current?.state ? /* @__PURE__ */ jsx("span", { className: "ev-pill ev-period__pill", "data-tone": current.stateTone ?? "neutral", children: current.state }) : null,
              /* @__PURE__ */ jsx(ChevronIcon, { dir: "down" })
            ]
          }
        ),
        children: years.map(([year, months], yearIndex) => /* @__PURE__ */ jsx(MenuGroup, { label: String(year), className: "ev-period__year", children: months.map((o) => {
          const m = Number(o.value.slice(5, 7));
          const selected = o.value === value;
          return /* @__PURE__ */ jsxs(
            MenuItem,
            {
              label: label(o),
              disabled: o.disabled,
              "aria-label": label(o),
              role: "menuitemradio",
              "aria-checked": selected,
              className: "ev-period__month",
              "data-period-cell": "",
              "data-year-index": yearIndex,
              "data-col": (m - 1) % 4 + 1,
              "data-row": Math.ceil(m / 4),
              "data-selected": selected ? "true" : void 0,
              onKeyDown: (event) => moveInGrid(event, getComputedStyle(event.currentTarget).direction === "rtl"),
              onClick: () => onChange(o.value),
              children: [
                /* @__PURE__ */ jsx("span", { className: "ev-period__month-name", children: monthShort(m, locale) }),
                o.state ? /* @__PURE__ */ jsx("span", { className: "ev-pill ev-period__pill", "data-tone": o.stateTone ?? "neutral", children: o.state }) : null
              ]
            },
            o.value
          );
        }) }, year))
      }
    ),
    /* @__PURE__ */ jsx(IconButton, { "aria-label": labels.next, title: labels.next, disabled: disabled || !next, onClick: () => next && onChange(next.value), children: /* @__PURE__ */ jsx(ChevronIcon, { dir: "right" }) })
  ] });
});
PeriodSwitcher.displayName = "PeriodSwitcher";
export {
  PeriodSwitcher,
  formatPeriodLong,
  isPeriod
};
//# sourceMappingURL=PeriodSwitcher.js.map
